// ============================================
// Path: /frontend/src/hooks/useAuth.js
// Purpose: Custom hook for authentication (JWT version)
// ============================================

import { useState, useEffect, createContext, useContext } from 'react';
import { authAPI } from '../api/auth.api.js';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is logged in on mount
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Verify token is still valid
          const data = await authAPI.getCurrentUser();
          setUser(data.user);
        } catch (err) {
          // Token invalid or expired
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setError(null);
      const data = await authAPI.login(credentials);
      setUser(data.user);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      throw err;
    }
  };

    const register = async (userData) => {
    try {
      setError(null);
      const data = await authAPI.register(userData);
      
      // Check if registration requires approval (no token sent)
      if (data.token) {
        // Auto-login for approved accounts
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
      } else {
        // Registration successful but needs approval
        console.log('[useAuth] Registration successful - awaiting approval');
        // Don't set user, don't store token
      }
      
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      throw err;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  };

  const refreshUser = async () => {
  try {
    const data = await authAPI.getCurrentUser();
    setUser(data.user);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    // ⭐ ADD: Broadcast event for other components to react
    window.dispatchEvent(new CustomEvent('userRefreshed', { 
      detail: data.user 
    }));
    
    console.log('[useAuth] User data refreshed:', data.user);
  } catch (err) {
    console.error('Refresh user error:', err);
  }
};

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser,
    isAdmin: user?.role === 'admin', // ADD THIS
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
